/**
 * Display formatters for the event detail page's fact bar and hero badges
 * (§ docs/EVENT_DETAIL_REDESIGN.md). Kept apart from the components so
 * they can be unit-tested without render infrastructure — this project
 * has no @testing-library/react at the root.
 *
 * Every function returns `null` (never an empty string or "undefined")
 * when it has nothing to say, so callers can drop the whole row.
 */

/**
 * "PT3H" -> "3 Stunden" / "3 hours"; "P5D" -> "5 Tage"; "P2DT4H" ->
 * "2 Tage 4 Std.". Days matter: a con runs for days, not hours, and an
 * hours-only reader silently dropped `P5D` to nothing.
 *
 * The frontmatter stores ISO-8601 because that is what schema.org's
 * `duration` needs verbatim; this is the human end of the same value.
 */
export const ISO_DURATION_RE = /^P(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?)?$/;

export function formatDuration(iso, lang) {
  if (!iso) return null;
  const match = ISO_DURATION_RE.exec(iso);
  if (!match) return null;
  const days = Number(match[1] || 0);
  const hours = Number(match[2] || 0);
  const minutes = Number(match[3] || 0);
  if (!days && !hours && !minutes) return null;

  const parts = [];
  if (days) {
    if (lang === "de") parts.push(days === 1 ? "1 Tag" : `${days} Tage`);
    else parts.push(days === 1 ? "1 day" : `${days} days`);
  }
  // Once a longer unit is in play, the shorter ones switch to their
  // abbreviated form — "5 Tage 4 Stunden 30 Minuten" reads as a recipe.
  const abbreviated = parts.length > 0;
  if (hours) {
    if (lang === "de") parts.push(abbreviated || minutes ? `${hours} Std.` : hours === 1 ? "1 Stunde" : `${hours} Stunden`);
    else parts.push(abbreviated || minutes ? `${hours} hr` : hours === 1 ? "1 hour" : `${hours} hours`);
  }
  if (minutes) {
    if (lang === "de") parts.push(parts.length > 0 ? `${minutes} Min.` : `${minutes} Minuten`);
    else parts.push(parts.length > 0 ? `${minutes} min` : `${minutes} minutes`);
  }
  return parts.join(" ");
}

/** Both bounds, only a lower one, or only an upper one — each reads
 * differently in each language, so this is a lookup rather than a
 * template with holes. */
export function ageRangeLabel({ ageMin, ageMax }, lang) {
  if (ageMin == null && ageMax == null) return null;
  if (ageMin != null && ageMax != null) {
    return lang === "de" ? `${ageMin}–${ageMax} Jahre` : `ages ${ageMin}–${ageMax}`;
  }
  if (ageMin != null) return lang === "de" ? `ab ${ageMin} Jahren` : `${ageMin}+`;
  return lang === "de" ? `bis ${ageMax} Jahre` : `up to ${ageMax}`;
}

/** "16:00" + "19:00" -> "16–19 Uhr" / "4–7 PM"-ish. German gets the
 * compact form the design shows; English keeps the 24-hour clock rather
 * than guessing at AM/PM for a German venue's opening time. */
export function timeRangeLabel({ startTime, endTime }, lang) {
  if (!startTime) return null;
  const trim = (t) => t.replace(/:00$/, "");
  if (!endTime) return lang === "de" ? `ab ${trim(startTime)} Uhr` : `from ${startTime}`;
  return lang === "de" ? `${trim(startTime)}–${trim(endTime)} Uhr` : `${startTime}–${endTime}`;
}

/**
 * Day heading for a multi-day running order: "Mi., 05. Aug. 2026".
 *
 * The weekday earns its place here in a way it doesn't elsewhere on the
 * site — on a five-day con, "Donnerstag" is how people actually navigate
 * the programme, more than the date is.
 */
export function formatScheduleDay(date, lang) {
  if (!date) return null;
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString(lang === "de" ? "de-DE" : "en-GB", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}
